import type { ContactFormData, PricingEstimationService } from '../../types';
import { SERVICES } from './estimateDisplay';
import { isDateOption } from './weddingDate';
import type { WeddingDateData } from './weddingDate';

export interface PersistedFormInput {
	guestCount: string;
	weddingDate: WeddingDateData;
	selectedServices: PricingEstimationService[];
	contactData?: ContactFormData;
}

const STORAGE_KEY = 'app:TKMarketplace:EstimateFormInput';

const VALID_SERVICES = new Set<string>(SERVICES.map((s) => s.key));

export const getPersistedFormInput = (): PersistedFormInput | null => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed: unknown = JSON.parse(raw);
		if (!isValidPersistedFormInput(parsed)) return null;
		return {
			guestCount: parsed.guestCount,
			weddingDate: parsed.weddingDate,
			selectedServices: parsed.selectedServices.filter(
				(s): s is PricingEstimationService => VALID_SERVICES.has(s),
			),
			...(parsed.contactData ? { contactData: parsed.contactData } : {}),
		};
	} catch {
		return null;
	}
};

export const setPersistedFormInput = (input: PersistedFormInput): void => {
	try {
		const toStore: PersistedFormInput = {
			guestCount: input.guestCount,
			weddingDate: input.weddingDate,
			selectedServices: input.selectedServices,
			contactData: input.contactData ?? getPersistedFormInput()?.contactData,
		};
		localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
	} catch {
		// storage full or unavailable — silently ignore
	}
};

const isValidWeddingDate = (value: unknown): value is WeddingDateData => {
	if (typeof value !== 'object' || value === null) return false;
	const wd = value as Record<string, unknown>;
	if (typeof wd.dateOption !== 'string') return false;
	if (wd.dateOption !== '' && !isDateOption(wd.dateOption)) return false;
	if (typeof wd.specificDate !== 'string') return false;
	if (typeof wd.month !== 'string') return false;
	if (typeof wd.year !== 'string') return false;
	return true;
};

const isValidContactData = (value: unknown): value is ContactFormData => {
	if (typeof value !== 'object' || value === null) return false;
	const cd = value as Record<string, unknown>;
	if (typeof cd.firstName !== 'string') return false;
	if (typeof cd.lastName !== 'string') return false;
	if (typeof cd.email !== 'string') return false;
	return true;
};

interface RawPersistedFormInput {
	guestCount: string;
	weddingDate: WeddingDateData;
	selectedServices: string[];
	contactData?: ContactFormData;
}

const isValidPersistedFormInput = (
	value: unknown,
): value is RawPersistedFormInput => {
	if (typeof value !== 'object' || value === null) return false;
	const v = value as Record<string, unknown>;
	if (typeof v.guestCount !== 'string') return false;
	if (!isValidWeddingDate(v.weddingDate)) return false;
	if (!Array.isArray(v.selectedServices)) return false;
	if (!v.selectedServices.every((s: unknown) => typeof s === 'string'))
		return false;
	if (v.contactData !== undefined && !isValidContactData(v.contactData))
		return false;
	return true;
};
